import { BaseAPIService } from '../../../../services/baseapiservice';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Base interface for all entities with audit fields
 */
export interface BaseEntity {
  id?: number;
  a_in?: boolean;
  clnt_id?: number;
  crte_usr_id?: number;
  updte_usr_id?: number;
  i_ts?: string;
  u_ts?: string;
  vrsn_nu?: number;
  createdby_name?: string;
  lastmodifiedby_name?: string;
}

/**
 * Application Master entity (hlpd_app_lst_t)
 */
export interface Application extends BaseEntity {
  hlpd_app_id?: number;
  hlpd_app_nm: string;
  hlpd_app_cd: string;
  dscn_tx?: string;
  sqnce_nu?: number;
  component_count?: number;
}

/**
 * Component Master entity (hlpd_cmpnt_lst_t)
 */
export interface Component extends BaseEntity {
  hlpd_cmpnt_id?: number;
  hlpd_app_id: number;
  hlpd_cmpnt_nm: string;
  hlpd_cmpnt_cd: string;
  dscn_tx?: string;
  sqnce_nu?: number;
  hlpd_app_nm?: string;
  hlpd_app_cd?: string;
}

/**
 * Sub Module Master entity (hlpd_sub_mdle_lst_t)
 *
 * Minimal table — it has no code, description, sequence or audit columns,
 * unlike every other hlpd_*_lst_t master. Only name + parent module + active.
 */
/**
 * A row from app_mnu_itm_lst_t. Root rows (prnt_mnu_itm_id null/0) are Modules;
 * their children are Sub Modules. Used by the New Ticket form's cascading dropdowns.
 */
export interface MenuItem {
  mnu_itm_id: number;
  mnu_itm_nm: string;
  display_name?: string;
  prnt_mnu_itm_id?: number | null;
  icn_tx?: string;
  app_id?: number;
}

export interface SubModule {
  hlpd_sub_mdle_id?: number;
  hlpd_sub_mdle_nm: string;
  hlpd_cmpnt_id: number;
  a_in?: boolean;
  i_ts?: string;
  u_ts?: string;
  // Joined, read-only
  hlpd_cmpnt_nm?: string;
  hlpd_cmpnt_cd?: string;
  hlpd_app_id?: number;
  hlpd_app_nm?: string;
}

/**
 * Sub Issue Type Master entity (hlpd_sub_issue_type_lst_t)
 * Child of a ticket type via hlpd_tckt_type_id.
 */
export interface SubIssueType extends BaseEntity {
  hlpd_sub_issue_type_id?: number;
  hlpd_sub_issue_type_nm: string;
  hlpd_sub_issue_type_cd: string;
  hlpd_tckt_type_id: number;
  dscn_tx?: string;
  sqnce_nu?: number;
  // Joined, read-only
  hlpd_tckt_type_nm?: string;
  hlpd_tckt_type_cd?: string;
}

/**
 * Mode entity — backs the "Mode" dropdown on the New Ticket form.
 * Sourced from ds_dtcp.app_cmpnt_lst_t, so it uses cmpnt_* columns (not hlpd_*).
 */
export interface Mode extends BaseEntity {
  cmpnt_id?: number;
  cmpnt_nm: string;
  dscn_tx?: string;
  cmpnt_ctgry_id?: number;
}

/**
 * Priority Master entity (hlpd_prrty_lst_t)
 */
export interface Priority extends BaseEntity {
  hlpd_prrty_id?: number;
  hlpd_prrty_nm: string;
  hlpd_prrty_cd: string;
  dscn_tx?: string;
  rspns_sla_hrs_nu?: number;
  rsln_sla_hrs_nu?: number;
  clr_cd?: string;
  is_dflt_in?: boolean;
  sqnce_nu?: number;
}

/**
 * Status Master entity (hlpd_sts_lst_t)
 */
export interface Status extends BaseEntity {
  hlpd_sts_id?: number;
  hlpd_sts_nm: string;
  hlpd_sts_cd: string;
  dscn_tx?: string;
  clr_cd?: string;
  is_dflt_in?: boolean;
  is_opn_in?: boolean;
  is_rslvd_in?: boolean;
  is_clsd_in?: boolean;
  sqnce_nu?: number;
}

/**
 * Category Master entity (hlpd_ctgry_lst_t)
 */
export interface Category extends BaseEntity {
  hlpd_ctgry_id?: number;
  hlpd_ctgry_nm: string;
  hlpd_ctgry_cd: string;
  hlpd_app_id?: number;
  prnt_ctgry_id?: number;
  dscn_tx?: string;
  sqnce_nu?: number;
  hlpd_app_nm?: string;
  hlpd_tckt_type_id?: number;
  hlpd_tckt_type_nm?: string;
  hlpd_tckt_type_cd?: string;
}

/**
 * Ticket Type Master entity (hlpd_tckt_type_lst_t)
 */
export interface TicketType extends BaseEntity {
  hlpd_tckt_type_id?: number;
  hlpd_tckt_type_nm: string;
  hlpd_tckt_type_cd: string;
  dscn_tx?: string;
  clr_cd?: string;
  icn_tx?: string;
  sqnce_nu?: number;
  custom_field_count?: number;
}

/**
 * Custom Field Master entity (hlpd_cstm_fld_lst_t)
 */
export interface CustomField extends BaseEntity {
  hlpd_cstm_fld_id?: number;
  hlpd_tckt_type_id: number;
  fld_nm_tx: string;
  fld_lbl_tx: string;
  fld_type_cd: string;
  is_rqrd_in?: boolean;
  dflt_val_tx?: string;
  optns_jn?: FieldOption[];
  vldtn_rgx_tx?: string;
  hlp_tx?: string;
  sqnce_nu?: number;
  hlpd_tckt_type_nm?: string;
}

/**
 * Field Option for dropdown/select custom fields
 */
export interface FieldOption {
  label: string;
  value: string;
}

/**
 * Ticket entity (hlpd_tckt_dtl_t)
 */
export interface Ticket extends BaseEntity {
  hlpd_tckt_id?: number;
  hlpd_tckt_nu?: string;
  sbj_tx: string;
  dscn_tx?: string;
  hlpd_app_id?: number;
  hlpd_cmpnt_id?: number;
  hlpd_ctgry_id?: number;
  hlpd_tckt_type_id?: number;
  hlpd_prrty_id?: number;
  hlpd_sts_id?: number;
  hlpd_clse_rsn_id?: number;
  rptd_by_usr_id?: number;
  /**
   * The reporter's tenant code, paired with rptd_by_usr_id — employee ids are allocated
   * per UDA, so the id alone does not identify the account. Null on tickets raised before
   * the column was recorded. TicketDetail uses the pair to decide whether the viewer is
   * the reporter, which limits them to Update Info + Close.
   */
  rptd_by_tnt_cd?: string | null;
  /** Module / Sub Module — ids into ds_dtcp.app_mnu_itm_lst_t (see MenuItem). */
  mnu_itm_id?: number;
  sub_mnu_itm_id?: number;
  dept_id?: number;
  ulb_id?: number;
  assgnd_team_id?: number;
  /** The working assignee a human picks (Assign / Move → Forward) — the IT developer. */
  assgnd_usr_id?: number;
  /** The UDA's Support (526) user, stamped server-side on DPO confirmation. */
  spt_usr_id?: number;
  rsln_nts_tx?: string;
  rsln_stps_tx?: string;
  rslvd_ts?: string;
  clsd_ts?: string;
  src_cd?: string;
  rtng_nu?: number;
  rtng_fdbck_tx?: string;
  sla_rspns_mt_in?: boolean;
  sla_rsln_mt_in?: boolean;
  days_open?: number;
  // Related fields
  hlpd_app_nm?: string;
  hlpd_app_cd?: string;
  hlpd_cmpnt_nm?: string;
  hlpd_cmpnt_cd?: string;
  /** Resolved server-side from cstm_flds_jn->>'sub_module' (id or free text). */
  sub_mdle_nm?: string;
  hlpd_ctgry_nm?: string;
  hlpd_ctgry_cd?: string;
  hlpd_tckt_type_nm?: string;
  hlpd_tckt_type_cd?: string;
  tckt_type_clr?: string;
  tckt_type_icn?: string;
  hlpd_prrty_nm?: string;
  hlpd_prrty_cd?: string;
  prrty_clr?: string;
  hlpd_sts_nm?: string;
  hlpd_sts_cd?: string;
  sts_clr?: string;
  is_opn_in?: boolean;
  is_clsd_in?: boolean;
  is_rslvd_in?: boolean;
  hlpd_clse_rsn_nm?: string;
  hlpd_clse_rsn_cd?: string;
  clse_rsn_clr?: string;
  rptd_by_usr_nm?: string;
  dept_nm?: string;
  ulb_nm?: string;
  assgnd_usr_nm?: string;
  spt_usr_nm?: string;
  assgnd_team_nm?: string;
  // Custom field values stored as JSON
  cstm_flds_jn?: Record<string, any>;
}

/**
 * Dashboard Summary
 */
/**
 * One inbox event — a row of hlpd_tckt_hstry_dtl_t joined to its ticket.
 * `ts_epoch` (seconds) is what the bell compares against its stored last-seen mark
 * to decide what's unread; `i_ts` is the same instant pre-formatted for display.
 */
export interface InboxItem {
  hlpd_tckt_hstry_id: number;
  hlpd_tckt_id: number;
  hlpd_tckt_nu: string;
  sbj_tx: string;
  actn_cd: string;
  new_sts_nm?: string | null;
  new_sts_cd?: string | null;
  new_sts_clr?: string | null;
  new_assgnd_usr_id?: number | null;
  crte_usr_id?: number | null;
  rptd_by_usr_id?: number | null;
  by_usr_nm?: string | null;
  to_usr_nm?: string | null;
  rptd_by_usr_nm?: string | null;
  i_ts: string;
  ts_epoch: number | string;
}

export interface DashboardSummary {
  total_tickets: number;
  open_tickets: number;
  closed_tickets: number;
  resolved_tickets: number;
  unassigned_tickets: number;
  /** Open + unresolved tickets past their priority's resolution SLA (rsln_sla_hrs_nu). */
  overdue_tickets?: number;
}

/**
 * Ticket Count by Status and Type (cross-tabulation)
 */
export interface StatusByTypeRow {
  hlpd_sts_id: number;
  hlpd_sts_nm: string;
  hlpd_sts_cd: string;
  clr_cd: string;
  sqnce_nu: number;
  ticket_types: Record<string, { count: number; type_id: number }>;
}

export interface StatusByTypeData {
  statuses: StatusByTypeRow[];
  ticketTypes: Array<{
    hlpd_tckt_type_id: number;
    hlpd_tckt_type_nm: string;
    hlpd_tckt_type_cd: string;
    clr_cd: string;
    sqnce_nu: number;
  }>;
}

/**
 * Ticket Count by Type
 */
export interface TicketCountByType {
  hlpd_tckt_type_id: number;
  hlpd_tckt_type_nm: string;
  hlpd_tckt_type_cd: string;
  clr_cd: string;
  icn_tx: string;
  total_tickets: number;
  open_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  unassigned_tickets: number;
}

/**
 * Ticket Count by Application
 */
export interface TicketCountByApplication {
  hlpd_app_id: number;
  hlpd_app_nm: string;
  hlpd_app_cd: string;
  total_tickets: number;
  open_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  unassigned_tickets: number;
}

/**
 * Ticket Count by Category
 */
export interface TicketCountByCategory {
  hlpd_ctgry_id: number;
  hlpd_ctgry_nm: string;
  hlpd_ctgry_cd: string;
  hlpd_app_id: number;
  total_tickets: number;
  open_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  unassigned_tickets: number;
}

/**
 * Ticket Count by Department
 */
export interface TicketCountByDepartment {
  dept_id: number;
  dept_nm: string;
  total_tickets: number;
  open_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  unassigned_tickets: number;
}

/**
 * FAQ Category Master entity (hlpd_faq_ctgry_lst_t)
 */
export interface FaqCategory extends BaseEntity {
  hlpd_faq_ctgry_id?: number;
  hlpd_faq_ctgry_nm: string;
  hlpd_faq_ctgry_cd: string;
  dscn_tx?: string;
  is_intrnl_in?: boolean;
  icn_tx?: string;
  sqnce_nu?: number;
  faq_cnt?: number;
  crte_usr_nm?: string;
  updte_usr_nm?: string;
}

/**
 * FAQ Master entity (hlpd_faq_lst_t)
 */
export interface Faq extends BaseEntity {
  hlpd_faq_id?: number;
  hlpd_faq_ctgry_id: number;
  qstn_tx: string;
  ansr_tx: string;
  hlpd_tckt_id?: number;
  hlpd_app_id?: number;
  is_intrnl_in?: boolean;
  is_pblshd_in?: boolean;
  vw_ct?: number;
  hlpfl_ct?: number;
  not_hlpfl_ct?: number;
  tgs_tx?: string;
  sqnce_nu?: number;
  // Joined fields
  hlpd_faq_ctgry_nm?: string;
  hlpd_faq_ctgry_cd?: string;
  hlpd_app_nm?: string;
  hlpd_app_cd?: string;
  crte_usr_nm?: string;
  updte_usr_nm?: string;
}

/**
 * Support Team Master entity (hlpd_sprt_team_lst_t)
 */
export interface SupportTeam extends BaseEntity {
  hlpd_sprt_team_id?: number;
  hlpd_sprt_team_nm: string;
  hlpd_sprt_team_cd: string;
  dscn_tx?: string;
  team_lead_usr_id?: number;
  dprtmnt_id?: number;
  eml_tx?: string;
  sqnce_nu?: number;
  team_lead_nm?: string;
  member_cnt?: number;
  crte_usr_nm?: string;
  updte_usr_nm?: string;
  members?: SupportTeamMember[];
}

/**
 * Support Team Member entity (hlpd_sprt_team_mbr_rel_t)
 */
export interface SupportTeamMember extends BaseEntity {
  hlpd_sprt_team_mbr_id?: number;
  hlpd_sprt_team_id: number;
  usr_id: number;
  rle_cd?: string;
  is_dspchtr_in?: boolean;
  max_tckt_ct?: number;
  usr_nm?: string;
  usr_email?: string;
  usr_mobile?: string;
  hlpd_sprt_team_nm?: string;
  hlpd_sprt_team_cd?: string;
  crte_usr_nm?: string;
  updte_usr_nm?: string;
}

/**
 * Close Reason Master entity (hlpd_clse_rsn_lst_t)
 */
export interface CloseReason extends BaseEntity {
  hlpd_clse_rsn_id?: number;
  hlpd_clse_rsn_nm: string;
  hlpd_clse_rsn_cd: string;
  dscn_tx?: string;
  rqrs_rsln_in?: boolean;
  icn_tx?: string;
  clr_cd?: string;
  is_dflt_in?: boolean;
  sqnce_nu?: number;
}

/**
 * Close Ticket Request
 */
export interface CloseTicketRequest {
  hlpd_clse_rsn_id: number;
  close_notes?: string;
}

/**
 * History Action Types
 */
export type HistoryActionType =
  | 'CREATED'
  | 'STATUS_CHANGED'
  | 'ASSIGNED'
  | 'REASSIGNED'
  | 'INFO_REQUESTED'
  | 'INFO_PROVIDED'
  | 'RESOLVED'
  | 'CLOSED'
  | 'REOPENED'
  | 'PRIORITY_CHANGED'
  | 'ESCALATED'
  | 'COMMENT_ADDED'
  | 'ATTACHMENT_ADDED';

/**
 * Ticket History/Timeline Entry
 */
export interface TicketHistoryEntry extends BaseEntity {
  hlpd_tckt_hstry_id?: number;
  hlpd_tckt_id: number;
  actn_cd: HistoryActionType;
  actn_dscn_tx?: string;

  // Status change details
  prev_sts_id?: number;
  prev_sts_nm?: string;
  prev_sts_clr?: string;
  new_sts_id?: number;
  new_sts_nm?: string;
  new_sts_clr?: string;

  // Assignment details
  prev_assgnd_usr_id?: number;
  prev_assgnd_usr_nm?: string;
  new_assgnd_usr_id?: number;
  new_assgnd_usr_nm?: string;
  prev_assgnd_team_id?: number;
  prev_assgnd_team_nm?: string;
  new_assgnd_team_id?: number;
  new_assgnd_team_nm?: string;

  // Priority change details
  prev_prrty_id?: number;
  prev_prrty_nm?: string;
  prev_prrty_clr?: string;
  new_prrty_id?: number;
  new_prrty_nm?: string;
  new_prrty_clr?: string;

  // Info request details
  info_rqst_tx?: string;
  info_rspns_tx?: string;
  info_rqst_ts?: string;
  info_rspns_ts?: string;
  is_info_pndng_in?: boolean;

  // Close reason
  hlpd_clse_rsn_id?: number;
  hlpd_clse_rsn_nm?: string;
  clse_rsn_clr?: string;
  clse_nts_tx?: string;

  // Resolution
  rsln_nts_tx?: string;

  // Metadata
  meta_jn?: Record<string, any>;

  // Audit
  crte_usr_nm?: string;
}

/**
 * Request Information payload
 */
export interface RequestInfoPayload {
  info_rqst_tx: string;
}

/**
 * Provide Information payload
 */
export interface ProvideInfoPayload {
  info_rspns_tx: string;
}

/**
 * Reopen Ticket payload
 */
export interface ReopenTicketPayload {
  reason?: string;
}

/**
 * User entity for support team selection
 */
export interface AvailableUser {
  id: number;
  name: string;
  emailid?: string;
  mobilenumber?: string;
  active?: boolean;
}

/**
 * Comment entity (hlpd_cmnt_dtl_t)
 */
export interface Comment extends BaseEntity {
  hlpd_cmnt_id?: number;
  hlpd_tckt_id: number;
  cmnt_tx: string;
  is_intrnl_in?: boolean;
  is_rsltn_in?: boolean;
  prnt_cmnt_id?: number;
  attchmnt_jn?: Attachment[];
  prnt_cmnt_tx?: string;
  crte_usr_nm?: string;
  updte_usr_nm?: string;
  reply_cnt?: number;
}

/**
 * Attachment entity
 */
export interface Attachment {
  file_nm: string;
  file_url: string;
  file_type?: string;
  file_size?: number;
  uploaded_at?: string;
}

/**
 * Comment Count response
 */
export interface CommentCount {
  total: number;
  internal_cnt: number;
  public_cnt: number;
}

/**
 * Pagination metadata
 */
export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/**
 * Server-side pagination metadata
 */
export interface ServerPagination {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

/**
 * Paginated tickets response (flat structure)
 * data: array of tickets
 * pagination: pagination info at same level
 */
export interface PaginatedTicketsResponse {
  data: Ticket[];
  pagination: ServerPagination;
}

/**
 * Ticket counts per status tab. Computed against the same filters as the page, minus
 * the selected status_type — so every tab shows a count, not just the active one.
 */
export interface TicketStatusCounts {
  all: number;
  open: number;
  resolved: number;
  closed: number;
  unassigned: number;
  overdue: number;
}

/**
 * API response for paginated tickets (flat structure)
 */
export interface PaginatedApiResponse {
  status: number;
  message?: string;
  data: Ticket[];
  pagination: ServerPagination;
  counts?: TicketStatusCounts;
}

/**
 * Filter option (for dropdowns)
 */
export interface FilterOption {
  value: number;
  label: string;
  clr_cd?: string;
  is_opn_in?: boolean;
  is_clsd_in?: boolean;
  is_rslvd_in?: boolean;
  hlpd_app_id?: number;
  team_id?: number;
}

/**
 * All filter options for tickets page
 */
export interface TicketFilterOptions {
  applications: FilterOption[];
  ticketTypes: FilterOption[];
  statuses: FilterOption[];
  priorities: FilterOption[];
  closeReasons: FilterOption[];
  categories: FilterOption[];
  supportTeams: FilterOption[];
  supportUsers: FilterOption[];
}

// ── Ageing Analysis ──────────────────────────────────────────────────────────

/** One Application row of the coarse ageing cross-tab (d1..d8 = the 8 day-buckets). */
export interface AgeingRow {
  application: string;
  app_id: number;
  d1: number; d2: number; d3: number; d4: number;
  d5: number; d6: number; d7: number; d8: number;
  total: number;
}

/**
 * Headline ticket summary. Invariant: resolved + assigned_uda + pending = bugs_created.
 *   assigned_uda = still open AND someone has picked it up
 *   pending      = still open AND nobody has taken it
 */
export interface BugReport {
  bugs_created: number;
  resolved: number;
  assigned_uda: number;
  pending: number;
  resolved_yesterday: number;
  yesterday: string;
  today: string;
}

/** One Module row of the fine ageing cross-tab (b1..b19). Grand total = BugReport.pending. */
export interface BugAgeingModuleRow {
  mnu_itm_id: number | null;
  mnu_itm_nm: string | null;
  b1: number; b2: number; b3: number; b4: number; b5: number; b6: number; b7: number;
  b8: number; b9: number; b10: number; b11: number; b12: number; b13: number;
  b14: number; b15: number; b16: number; b17: number; b18: number; b19: number;
  total: number;
}

/** KPI-card categories the overview endpoints understand (see overviewStatusCond in TicketMdl). */
export type OverviewStatus = 'open' | 'resolved' | 'closed' | 'assigned' | 'unassigned' | 'forwarded' | 'returned' | 'overdue';

/** Which axis the overview counts are grouped along (the By Module / By UDA tabs). */
export type OverviewGroupBy = 'uda' | 'module' | 'submodule';

/**
 * The Support Tickets filter bar. One object drives the whole screen — KPI cards,
 * grouped counts and the ticket grid all take the same filters, so they cannot
 * disagree. `from`/`to` are inclusive YYYY-MM-DD dates.
 */
export interface OverviewFilters {
  uda_id?: number | '';
  mnu_itm_id?: number | '';
  sub_mnu_itm_id?: number | '';
  from?: string;
  to?: string;
  status?: OverviewStatus | '';
  /** Narrow to one engineer's assigned tickets — backs the "My Tickets" table for IT (525). */
  assgnd_usr_id?: number | '';
  /** Narrow to one support user's tickets — backs "My Tickets" for Support (526), who
   *  own tickets via spt_usr_id rather than assgnd_usr_id. */
  spt_usr_id?: number | '';
}

export interface OverviewSummary {
  total_tickets: number;
  /** Distinct UDAs carrying a ticket in the current filter window (not the full master list). */
  uda_count: number;
  open_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  assigned_tickets: number;
  unassigned_tickets: number;
  forwarded_tickets: number;
  returned_tickets: number;
  overdue_tickets: number;
}

/** One row of the By Module / By UDA grid. Only the id/name pair for the active axis is set. */
export interface OverviewGroupRow {
  uda_id?: number | null;
  uda_nm?: string | null;
  mnu_itm_id?: number | null;
  mnu_itm_nm?: string | null;
  sub_mnu_itm_id?: number | null;
  sub_mnu_itm_nm?: string | null;
  total: number;
  open: number;
  forwarded: number;
  returned: number;
  closed: number;
  overdue: number;
}

/**
 * Ticket filter parameters
 */
export interface TicketFilters {
  // Pagination
  page?: number;
  page_size?: number;

  // Sorting
  sort_by?: string;
  sort_order?: 'asc' | 'desc';

  // Filter params
  hlpd_app_id?: number;
  hlpd_ctgry_id?: number;
  hlpd_tckt_type_id?: number;
  hlpd_prrty_id?: number;
  hlpd_sts_id?: number | number[];
  hlpd_clse_rsn_id?: number;
  assgnd_usr_id?: number;
  assgnd_team_id?: number;
  rptd_by_usr_id?: number;

  // Quick status filters
  status_type?: 'all' | 'open' | 'closed' | 'resolved' | 'unassigned' | 'overdue';

  // Date range
  created_from?: string;
  created_to?: string;

  // Days open
  days_open_min?: number;
  days_open_max?: number;

  // Feedback
  has_feedback?: 'yes' | 'no';
  rating_min?: number;
  rating_max?: number;

  // SLA
  sla_breach?: 'response' | 'resolution' | 'any';

  // Search
  search?: string;
  hlpd_tckt_nu?: string;

  // Active
  a_in?: boolean;
}

/**
 * Paginated comments response
 */
export interface CommentsResponse {
  data: Comment[];
  pagination: Pagination;
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  status: number;
  message?: string;
  data: T;
  errors?: any[];
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class HelpdeskService extends BaseAPIService {
  private basePath = '/helpdesk';

  // Helper to extract data from axios response
  private extractData<T>(response: any): ApiResponse<T> {
    return response.data;
  }

  // ============================================================================
  // APPLICATION MASTER APIS
  // ============================================================================

  async getAllApplications(): Promise<ApiResponse<Application[]>> {
    const response = await this.get(`${this.basePath}/application`);
    return this.extractData(response);
  }

  async getActiveApplications(): Promise<ApiResponse<Application[]>> {
    const response = await this.get(`${this.basePath}/application/active`);
    return this.extractData(response);
  }

  async getApplicationById(id: number): Promise<ApiResponse<Application>> {
    const response = await this.get(`${this.basePath}/application/${id}`);
    return this.extractData(response);
  }

  async saveApplication(data: Partial<Application>): Promise<ApiResponse<Application>> {
    const response = await this.post(`${this.basePath}/application`, data);
    return this.extractData(response);
  }

  async deleteApplication(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/application/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // COMPONENT MASTER APIS
  // ============================================================================

  async getAllComponents(): Promise<ApiResponse<Component[]>> {
    const response = await this.get(`${this.basePath}/component`);
    return this.extractData(response);
  }

  async getComponentsByApplication(appId: number): Promise<ApiResponse<Component[]>> {
    const response = await this.get(`${this.basePath}/component/application/${appId}`);
    return this.extractData(response);
  }

  async getActiveComponentsByApplication(appId: number): Promise<ApiResponse<Component[]>> {
    const response = await this.get(`${this.basePath}/component/application/${appId}/active`);
    return this.extractData(response);
  }

  async getComponentById(id: number): Promise<ApiResponse<Component>> {
    const response = await this.get(`${this.basePath}/component/${id}`);
    return this.extractData(response);
  }

  async saveComponent(data: Partial<Component>): Promise<ApiResponse<Component>> {
    const response = await this.post(`${this.basePath}/component`, data);
    return this.extractData(response);
  }

  async deleteComponent(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/component/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // SUB MODULE MASTER APIS (hlpd_sub_mdle_lst_t)
  // ============================================================================

  async getAllSubModules(): Promise<ApiResponse<SubModule[]>> {
    const response = await this.get(`${this.basePath}/sub-module`);
    return this.extractData(response);
  }

  /** All active sub-modules; each row carries hlpd_cmpnt_id for client-side filtering. */
  async getActiveSubModules(): Promise<ApiResponse<SubModule[]>> {
    const response = await this.get(`${this.basePath}/sub-module/active`);
    return this.extractData(response);
  }

  async getSubModulesByComponent(componentId: number): Promise<ApiResponse<SubModule[]>> {
    const response = await this.get(`${this.basePath}/sub-module/component/${componentId}`);
    return this.extractData(response);
  }

  /**
   * Module dropdown — root menu items (prnt_mnu_itm_id IS NULL) from app_mnu_itm_lst_t.
   * Served by erp-admin, not helpdesk: the helpdesk reuses the app's own menu tree
   * rather than its own hlpd_cmpnt_lst_t / hlpd_sub_mdle_lst_t masters.
   */
  async getMenuModules(): Promise<ApiResponse<MenuItem[]>> {
    const response = await this.get('/erp-admin/menu-items/parents');
    return this.extractData(response);
  }

  /**
   * Module dropdown scoped to the LOGGED-IN user's menu profile — the same
   * role/profile-driven root modules shown in the sidebar (erp-auth/menu),
   * not the whole app_mnu_itm_lst_t tree. Used by the New Ticket / Report Issue
   * forms so a user only raises tickets against modules they can actually see.
   */
  async getMyMenuModules(): Promise<ApiResponse<MenuItem[]>> {
    const response = await this.get('/erp-auth/menu/modules');
    return this.extractData(response);
  }

  /** Sub Module dropdown — children of the chosen module (prnt_mnu_itm_id = moduleId). */
  async getMenuSubModules(moduleId: number): Promise<ApiResponse<MenuItem[]>> {
    const response = await this.get(`/erp-admin/menu-items/${moduleId}/children`);
    return this.extractData(response);
  }

  /**
   * Resolve a captured app route (e.g. "/app/utility/ds-helpdesk/...") → the menu
   * Module (parent) + Sub-Module (child) it belongs to. Used by the New Ticket form
   * to auto-select Module / Sub Module from where a screenshot was taken.
   * Returns { mdle_itm_id, mdle_itm_nm, sub_mdle_itm_id, sub_mdle_itm_nm } under .data.
   */
  async resolveMenuByPath(path: string): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/menu/resolve?path=${encodeURIComponent(path)}`);
    return this.extractData(response);
  }

  async getSubModuleById(id: number): Promise<ApiResponse<SubModule>> {
    const response = await this.get(`${this.basePath}/sub-module/${id}`);
    return this.extractData(response);
  }

  async saveSubModule(data: Partial<SubModule>): Promise<ApiResponse<SubModule>> {
    const response = await this.post(`${this.basePath}/sub-module`, data);
    return this.extractData(response);
  }

  async deleteSubModule(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/sub-module/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // SUB ISSUE TYPE MASTER APIS (hlpd_sub_issue_type_lst_t)
  // ============================================================================

  async getAllSubIssueTypes(): Promise<ApiResponse<SubIssueType[]>> {
    const response = await this.get(`${this.basePath}/sub-issue-type`);
    return this.extractData(response);
  }

  /** All active sub-issue-types; each row carries hlpd_tckt_type_id for filtering. */
  async getActiveSubIssueTypes(): Promise<ApiResponse<SubIssueType[]>> {
    const response = await this.get(`${this.basePath}/sub-issue-type/active`);
    return this.extractData(response);
  }

  async getSubIssueTypesByTicketType(ticketTypeId: number): Promise<ApiResponse<SubIssueType[]>> {
    const response = await this.get(`${this.basePath}/sub-issue-type/ticket-type/${ticketTypeId}`);
    return this.extractData(response);
  }

  async getSubIssueTypeById(id: number): Promise<ApiResponse<SubIssueType>> {
    const response = await this.get(`${this.basePath}/sub-issue-type/${id}`);
    return this.extractData(response);
  }

  async saveSubIssueType(data: Partial<SubIssueType>): Promise<ApiResponse<SubIssueType>> {
    const response = await this.post(`${this.basePath}/sub-issue-type`, data);
    return this.extractData(response);
  }

  async deleteSubIssueType(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/sub-issue-type/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // PRIORITY MASTER APIS
  // ============================================================================

  async getAllPriorities(): Promise<ApiResponse<Priority[]>> {
    const response = await this.get(`${this.basePath}/priority`);
    return this.extractData(response);
  }

  async getActivePriorities(): Promise<ApiResponse<Priority[]>> {
    const response = await this.get(`${this.basePath}/priority/active`);
    return this.extractData(response);
  }

  async getActiveModes(): Promise<ApiResponse<Mode[]>> {
    const response = await this.get(`${this.basePath}/mode/active`);
    return this.extractData(response);
  }

  async getPriorityById(id: number): Promise<ApiResponse<Priority>> {
    const response = await this.get(`${this.basePath}/priority/${id}`);
    return this.extractData(response);
  }

  async savePriority(data: Partial<Priority>): Promise<ApiResponse<Priority>> {
    const response = await this.post(`${this.basePath}/priority`, data);
    return this.extractData(response);
  }

  async deletePriority(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/priority/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // STATUS MASTER APIS
  // ============================================================================

  async getAllStatuses(): Promise<ApiResponse<Status[]>> {
    const response = await this.get(`${this.basePath}/status`);
    return this.extractData(response);
  }

  async getActiveStatuses(): Promise<ApiResponse<Status[]>> {
    const response = await this.get(`${this.basePath}/status/active`);
    return this.extractData(response);
  }

  async getStatusById(id: number): Promise<ApiResponse<Status>> {
    const response = await this.get(`${this.basePath}/status/${id}`);
    return this.extractData(response);
  }

  async saveStatus(data: Partial<Status>): Promise<ApiResponse<Status>> {
    const response = await this.post(`${this.basePath}/status`, data);
    return this.extractData(response);
  }

  async deleteStatus(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/status/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // CATEGORY MASTER APIS
  // ============================================================================

  async getAllCategories(): Promise<ApiResponse<Category[]>> {
    const response = await this.get(`${this.basePath}/category`);
    return this.extractData(response);
  }

  async getActiveCategories(): Promise<ApiResponse<Category[]>> {
    const response = await this.get(`${this.basePath}/category/active`);
    return this.extractData(response);
  }

  async getCategoryById(id: number): Promise<ApiResponse<Category>> {
    const response = await this.get(`${this.basePath}/category/${id}`);
    return this.extractData(response);
  }

  async getCategoriesByApplication(appId: number): Promise<ApiResponse<Category[]>> {
    const response = await this.get(`${this.basePath}/category/application/${appId}`);
    return this.extractData(response);
  }

  async getCategoriesByTicketType(ticketTypeId: number): Promise<ApiResponse<Category[]>> {
    const response = await this.get(`${this.basePath}/category/ticket-type/${ticketTypeId}`);
    return this.extractData(response);
  }

  async saveCategory(data: Partial<Category>): Promise<ApiResponse<Category>> {
    const response = await this.post(`${this.basePath}/category`, data);
    return this.extractData(response);
  }

  async deleteCategory(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/category/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // TICKET TYPE MASTER APIS
  // ============================================================================

  async getAllTicketTypes(): Promise<ApiResponse<TicketType[]>> {
    const response = await this.get(`${this.basePath}/ticket-type`);
    return this.extractData(response);
  }

  async getActiveTicketTypes(): Promise<ApiResponse<TicketType[]>> {
    const response = await this.get(`${this.basePath}/ticket-type/active`);
    return this.extractData(response);
  }

  async getTicketTypeById(id: number): Promise<ApiResponse<TicketType>> {
    const response = await this.get(`${this.basePath}/ticket-type/${id}`);
    return this.extractData(response);
  }

  async saveTicketType(data: Partial<TicketType>): Promise<ApiResponse<TicketType>> {
    const response = await this.post(`${this.basePath}/ticket-type`, data);
    return this.extractData(response);
  }

  async deleteTicketType(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/ticket-type/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // CUSTOM FIELD MASTER APIS
  // ============================================================================

  async getAllCustomFields(): Promise<ApiResponse<CustomField[]>> {
    const response = await this.get(`${this.basePath}/custom-field`);
    return this.extractData(response);
  }

  async getCustomFieldsByTicketType(ticketTypeId: number): Promise<ApiResponse<CustomField[]>> {
    const response = await this.get(`${this.basePath}/custom-field/ticket-type/${ticketTypeId}`);
    return this.extractData(response);
  }

  async getActiveCustomFieldsByTicketType(ticketTypeId: number): Promise<ApiResponse<CustomField[]>> {
    const response = await this.get(`${this.basePath}/custom-field/ticket-type/${ticketTypeId}/active`);
    return this.extractData(response);
  }

  async getCustomFieldById(id: number): Promise<ApiResponse<CustomField>> {
    const response = await this.get(`${this.basePath}/custom-field/${id}`);
    return this.extractData(response);
  }

  async saveCustomField(data: Partial<CustomField>): Promise<ApiResponse<CustomField>> {
    const response = await this.post(`${this.basePath}/custom-field`, data);
    return this.extractData(response);
  }

  async updateCustomFieldSequence(fields: Array<{id: number, sqnce_nu: number}>): Promise<ApiResponse<CustomField[]>> {
    const response = await this.put(`${this.basePath}/custom-field/reorder`, { fields });
    return this.extractData(response);
  }

  async deleteCustomField(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/custom-field/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // TICKET TRANSACTION APIS
  // ============================================================================

  async getAllTickets(filters?: Record<string, any>): Promise<ApiResponse<Ticket[]>> {
    const queryString = filters ? '?' + new URLSearchParams(filters).toString() : '';
    const response = await this.get(`${this.basePath}/ticket${queryString}`);
    return this.extractData(response);
  }

  async getOpenTickets(): Promise<ApiResponse<Ticket[]>> {
    const response = await this.get(`${this.basePath}/ticket/open`);
    return this.extractData(response);
  }

  async getMyTickets(): Promise<ApiResponse<Ticket[]>> {
    const response = await this.get(`${this.basePath}/ticket/my`);
    return this.extractData(response);
  }

  async getAssignedTickets(agentId: number): Promise<ApiResponse<Ticket[]>> {
    const response = await this.get(`${this.basePath}/ticket/assigned/${agentId}`);
    return this.extractData(response);
  }

  async getTicketById(id: number): Promise<ApiResponse<Ticket>> {
    const response = await this.get(`${this.basePath}/ticket/${id}`);
    return this.extractData(response);
  }

  async saveTicket(data: Partial<Ticket>): Promise<ApiResponse<Ticket>> {
    const response = await this.post(`${this.basePath}/ticket`, data);
    return this.extractData(response);
  }

  async updateTicketStatus(ticketId: number, statusId: number, actn_dscn_tx?: string): Promise<ApiResponse<Ticket>> {
    const body: Record<string, any> = { hlpd_sts_id: statusId };
    if (actn_dscn_tx) body.actn_dscn_tx = actn_dscn_tx;
    const response = await this.put(`${this.basePath}/ticket/${ticketId}/status`, body);
    return this.extractData(response);
  }

  async assignTicket(ticketId: number, agentId: number, teamId?: number): Promise<ApiResponse<Ticket>> {
    const response = await this.put(`${this.basePath}/ticket/${ticketId}/assign`, {
      asgnd_agnt_id: agentId,
      assgnd_team_id: teamId
    });
    return this.extractData(response);
  }

  // An IT engineer picks (self-claims) a support-queue ticket to work on — the server
  // assigns it to the caller and moves it to the FORWARDED status. The assignee comes
  // from the session, so the body is empty.
  async pickTicket(ticketId: number): Promise<ApiResponse<Ticket>> {
    const response = await this.put(`${this.basePath}/ticket/${ticketId}/pick`, {});
    return this.extractData(response);
  }

  async resolveTicket(ticketId: number, resolution: string): Promise<ApiResponse<Ticket>> {
    const response = await this.put(`${this.basePath}/ticket/${ticketId}/resolve`, { rsltn_tx: resolution });
    return this.extractData(response);
  }

  async deleteTicket(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/ticket/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // OVERVIEW APIS (Support Tickets screen)
  // ============================================================================
  // Cross-UDA by default; the filter bar narrows the whole screen at once, so the
  // KPI cards, the By Module / By UDA counts and the ticket grid always agree.

  private overviewQuery(filters: OverviewFilters = {}): string {
    const p = new URLSearchParams();
    if (filters.uda_id) p.set('uda_id', String(filters.uda_id));
    if (filters.mnu_itm_id) p.set('mnu_itm_id', String(filters.mnu_itm_id));
    if (filters.sub_mnu_itm_id) p.set('sub_mnu_itm_id', String(filters.sub_mnu_itm_id));
    if (filters.from) p.set('from', filters.from);
    if (filters.to) p.set('to', filters.to);
    if (filters.status) p.set('status', filters.status);
    if (filters.assgnd_usr_id) p.set('assgnd_usr_id', String(filters.assgnd_usr_id));
    // Support (526) "My Tickets" is scoped by spt_usr_id (their UDA's support user), not
    // assgnd_usr_id — without this the filter was dropped and the server returned all rows.
    if (filters.spt_usr_id) p.set('spt_usr_id', String(filters.spt_usr_id));
    return p.toString();
  }

  // ============================================================================
  // AGEING ANALYSIS APIS
  // ============================================================================
  // No params — all three are scoped server-side to the caller's UDA.

  /** Pending (open) tickets as Application x days-open buckets. */
  async getAgeingAnalysis(): Promise<ApiResponse<AgeingRow[]>> {
    const response = await this.get(`${this.basePath}/ageing-analysis`);
    return this.extractData(response);
  }

  /** Headline summary: created / resolved / assigned / pending / resolved-yesterday. */
  async getBugReport(): Promise<ApiResponse<BugReport>> {
    const response = await this.get(`${this.basePath}/bug-report`);
    return this.extractData(response);
  }

  /** Unclaimed (open + unassigned) tickets as Module x fine day-buckets. */
  async getBugAgeingByModule(): Promise<ApiResponse<BugAgeingModuleRow[]>> {
    const response = await this.get(`${this.basePath}/bug-ageing-module`);
    return this.extractData(response);
  }

  /**
   * UDA dropdown source — every active UDA (ds_uda_lst_t), not just the ones that
   * happen to have tickets, so a UDA with zero tickets is still selectable.
   * Lives in erp-admin, not helpdesk, hence the absolute path.
   */
  async getUdas(): Promise<ApiResponse<Array<{ uda_id: number; uda_cd: string; uda_nm: string }>>> {
    const response = await this.get('/erp-admin/uda');
    return this.extractData(response);
  }

  /** KPI counts honouring the filter bar. */
  async getOverviewSummary(filters: OverviewFilters = {}): Promise<ApiResponse<OverviewSummary>> {
    const qs = this.overviewQuery(filters);
    const response = await this.get(`${this.basePath}/overview/summary${qs ? `?${qs}` : ''}`);
    return this.extractData(response);
  }

  /** Grouped ticket counts behind the By Module / By UDA tabs. */
  async getOverviewGroup(by: OverviewGroupBy, filters: OverviewFilters = {}): Promise<ApiResponse<OverviewGroupRow[]>> {
    const qs = this.overviewQuery(filters);
    const response = await this.get(`${this.basePath}/overview/group?by=${by}${qs ? `&${qs}` : ''}`);
    return this.extractData(response);
  }

  /**
   * Filtered ticket rows for the All Tickets grid and the Work Queue.
   * The server caps page_size at 200, so `pagination.total_count` is the honest row count
   * even when fewer rows come back — the screen reports the shortfall rather than
   * silently showing a truncated list.
   *
   * NOTE: formatSucessRes nests a controller's extras under `extra`, so pagination arrives
   * as `extra.pagination` — NOT at the response root like the older /ticket/paginated
   * endpoint, which hand-rolls its own response. Normalised here so callers see one shape.
   */
  async getOverviewTickets(filters: OverviewFilters = {}, page = 1, pageSize = 200): Promise<PaginatedApiResponse> {
    const p = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    const qs = this.overviewQuery(filters);
    const response = await this.get(`${this.basePath}/overview/tickets?${p.toString()}${qs ? `&${qs}` : ''}`);
    const env = this.extractData(response) as any;
    return { ...env, pagination: env?.extra?.pagination ?? env?.pagination ?? {} } as PaginatedApiResponse;
  }

  // ============================================================================
  // DASHBOARD APIS
  // ============================================================================

  /** @param allUdas count across every UDA instead of the caller's (Support Tickets). */
  async getDashboardSummary(allUdas = false): Promise<ApiResponse<DashboardSummary[]>> {
    const qs = allUdas ? '?all_udas=true' : '';
    const response = await this.get(`${this.basePath}/dashboard/summary${qs}`);
    return this.extractData(response);
  }

  /**
   * Server-side chart aggregates for the Help Desk dashboard — counted over every
   * ticket in scope, not just the rows the page happened to load.
   * Returns { overdue, byStatus, byPriority, byCategory, byApplication, byType,
   *           byModule, aging, recent }.
   */
  async getDashboardAggregates(): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/dashboard/aggregates`);
    return this.extractData(response);
  }

  /**
   * Inbox feed for the notification bell — recent ticket workflow events relevant to
   * the caller, newest first (max 50). The 'user' feed is the full activity trail for
   * tickets I reported (INCLUDING my own actions); the 'support' feed excludes my own;
   * the 'queue' feed is jurisdiction-wide activity for the Support Tickets screen; the
   * 'dpo' feed is the DPO / State user's action queue.
   * @param scope 'user' = tickets I reported; 'support' = tickets assigned to me;
   *              'queue' = every ticket in my jurisdiction (UDA); 'dpo' = tickets
   *              awaiting DPO confirmation or returned to support (DPO/State only —
   *              the server falls back to 'user' for anyone else).
   */
  async getInbox(scope: 'user' | 'support' | 'queue' | 'dpo' = 'user'): Promise<ApiResponse<InboxItem[]>> {
    const response = await this.get(`${this.basePath}/inbox?scope=${scope}`);
    return this.extractData(response);
  }

  async getStatusByType(): Promise<ApiResponse<StatusByTypeData>> {
    const response = await this.get(`${this.basePath}/dashboard/status-by-type`);
    return this.extractData(response);
  }

  async getTicketCountByType(): Promise<ApiResponse<TicketCountByType[]>> {
    const response = await this.get(`${this.basePath}/dashboard/by-type`);
    return this.extractData(response);
  }

  async getTicketCountByApplication(): Promise<ApiResponse<TicketCountByApplication[]>> {
    const response = await this.get(`${this.basePath}/dashboard/by-application`);
    return this.extractData(response);
  }

  async getTicketCountByCategory(appId: number): Promise<ApiResponse<TicketCountByCategory[]>> {
    const response = await this.get(`${this.basePath}/dashboard/by-category/${appId}`);
    return this.extractData(response);
  }

  async getTicketCountByDepartment(): Promise<ApiResponse<TicketCountByDepartment[]>> {
    const response = await this.get(`${this.basePath}/dashboard/by-department`);
    return this.extractData(response);
  }

  // ============================================================================
  // FAQ CATEGORY MASTER APIS
  // ============================================================================

  async getAllFaqCategories(): Promise<ApiResponse<FaqCategory[]>> {
    const response = await this.get(`${this.basePath}/faq-category`);
    return this.extractData(response);
  }

  async getActiveFaqCategories(): Promise<ApiResponse<FaqCategory[]>> {
    const response = await this.get(`${this.basePath}/faq-category/active`);
    return this.extractData(response);
  }

  async getInternalFaqCategories(): Promise<ApiResponse<FaqCategory[]>> {
    const response = await this.get(`${this.basePath}/faq-category/internal`);
    return this.extractData(response);
  }

  async getExternalFaqCategories(): Promise<ApiResponse<FaqCategory[]>> {
    const response = await this.get(`${this.basePath}/faq-category/external`);
    return this.extractData(response);
  }

  async getFaqCategoryById(id: number): Promise<ApiResponse<FaqCategory>> {
    const response = await this.get(`${this.basePath}/faq-category/${id}`);
    return this.extractData(response);
  }

  async saveFaqCategory(data: Partial<FaqCategory>): Promise<ApiResponse<FaqCategory>> {
    const response = await this.post(`${this.basePath}/faq-category`, data);
    return this.extractData(response);
  }

  async deleteFaqCategory(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/faq-category/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // FAQ MASTER APIS
  // ============================================================================

  async getAllFaqs(): Promise<ApiResponse<Faq[]>> {
    const response = await this.get(`${this.basePath}/faq`);
    return this.extractData(response);
  }

  async getActiveFaqs(): Promise<ApiResponse<Faq[]>> {
    const response = await this.get(`${this.basePath}/faq/active`);
    return this.extractData(response);
  }

  async getInternalFaqs(): Promise<ApiResponse<Faq[]>> {
    const response = await this.get(`${this.basePath}/faq/internal`);
    return this.extractData(response);
  }

  async getExternalFaqs(): Promise<ApiResponse<Faq[]>> {
    const response = await this.get(`${this.basePath}/faq/external`);
    return this.extractData(response);
  }

  async getPublishedFaqs(): Promise<ApiResponse<Faq[]>> {
    const response = await this.get(`${this.basePath}/faq/published`);
    return this.extractData(response);
  }

  async searchFaqs(query: string): Promise<ApiResponse<Faq[]>> {
    const response = await this.get(`${this.basePath}/faq/search?q=${encodeURIComponent(query)}`);
    return this.extractData(response);
  }

  async getFaqsByApplication(appId: number): Promise<ApiResponse<Faq[]>> {
    const response = await this.get(`${this.basePath}/faq/application/${appId}`);
    return this.extractData(response);
  }

  async getFaqsByCategory(categoryId: number): Promise<ApiResponse<Faq[]>> {
    const response = await this.get(`${this.basePath}/faq/category/${categoryId}`);
    return this.extractData(response);
  }

  async getFaqById(id: number): Promise<ApiResponse<Faq>> {
    const response = await this.get(`${this.basePath}/faq/${id}`);
    return this.extractData(response);
  }

  async saveFaq(data: Partial<Faq>): Promise<ApiResponse<Faq>> {
    const response = await this.post(`${this.basePath}/faq`, data);
    return this.extractData(response);
  }

  async incrementFaqViewCount(id: number): Promise<ApiResponse<Faq>> {
    const response = await this.put(`${this.basePath}/faq/${id}/view`, {});
    return this.extractData(response);
  }

  async markFaqHelpful(id: number): Promise<ApiResponse<Faq>> {
    const response = await this.put(`${this.basePath}/faq/${id}/helpful`, {});
    return this.extractData(response);
  }

  async markFaqNotHelpful(id: number): Promise<ApiResponse<Faq>> {
    const response = await this.put(`${this.basePath}/faq/${id}/not-helpful`, {});
    return this.extractData(response);
  }

  async toggleFaqPublish(id: number, publish: boolean): Promise<ApiResponse<Faq>> {
    const response = await this.put(`${this.basePath}/faq/${id}/publish`, { publish });
    return this.extractData(response);
  }

  async deleteFaq(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/faq/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // SUPPORT TEAM MASTER APIS
  // ============================================================================

  async getAllSupportTeams(): Promise<ApiResponse<SupportTeam[]>> {
    const response = await this.get(`${this.basePath}/support-team`);
    return this.extractData(response);
  }

  async getActiveSupportTeams(): Promise<ApiResponse<SupportTeam[]>> {
    const response = await this.get(`${this.basePath}/support-team/active`);
    return this.extractData(response);
  }

  async getSupportTeamById(id: number): Promise<ApiResponse<SupportTeam>> {
    const response = await this.get(`${this.basePath}/support-team/${id}`);
    return this.extractData(response);
  }

  async getSupportTeamWithMembers(id: number): Promise<ApiResponse<SupportTeam>> {
    const response = await this.get(`${this.basePath}/support-team/${id}/with-members`);
    return this.extractData(response);
  }

  async getAvailableUsers(): Promise<ApiResponse<AvailableUser[]>> {
    const response = await this.get(`${this.basePath}/support-team/users`);
    return this.extractData(response);
  }

  // Only users holding the "IT Team" role — the assignable engineers. Distinct from
  // getAvailableUsers(), which returns every active user and is what the Support Team
  // master screens need when picking members.
  async getItTeamUsers(): Promise<ApiResponse<AvailableUser[]>> {
    const response = await this.get(`${this.basePath}/support-team/it-team-users`);
    return this.extractData(response);
  }

  async saveSupportTeam(data: Partial<SupportTeam>): Promise<ApiResponse<SupportTeam>> {
    const response = await this.post(`${this.basePath}/support-team`, data);
    return this.extractData(response);
  }

  async deleteSupportTeam(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/support-team/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // SUPPORT TEAM MEMBER APIS
  // ============================================================================

  async getTeamMembers(teamId: number): Promise<ApiResponse<SupportTeamMember[]>> {
    const response = await this.get(`${this.basePath}/team-member/team/${teamId}`);
    return this.extractData(response);
  }

  async getActiveTeamMembers(teamId: number): Promise<ApiResponse<SupportTeamMember[]>> {
    const response = await this.get(`${this.basePath}/team-member/team/${teamId}/active`);
    return this.extractData(response);
  }

  async getTeamDispatchers(teamId: number): Promise<ApiResponse<SupportTeamMember[]>> {
    const response = await this.get(`${this.basePath}/team-member/team/${teamId}/dispatchers`);
    return this.extractData(response);
  }

  async getUserTeams(userId: number): Promise<ApiResponse<SupportTeamMember[]>> {
    const response = await this.get(`${this.basePath}/team-member/user/${userId}`);
    return this.extractData(response);
  }

  async getTeamMemberById(id: number): Promise<ApiResponse<SupportTeamMember>> {
    const response = await this.get(`${this.basePath}/team-member/${id}`);
    return this.extractData(response);
  }

  async saveTeamMember(data: Partial<SupportTeamMember>): Promise<ApiResponse<SupportTeamMember>> {
    const response = await this.post(`${this.basePath}/team-member`, data);
    return this.extractData(response);
  }

  async addMultipleTeamMembers(teamId: number, userIds: number[]): Promise<ApiResponse<SupportTeamMember[]>> {
    const response = await this.post(`${this.basePath}/team-member/bulk`, {
      hlpd_sprt_team_id: teamId,
      user_ids: userIds
    });
    return this.extractData(response);
  }

  async deleteTeamMember(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/team-member/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // COMMENT TRANSACTION APIS
  // ============================================================================

  async getCommentsByTicket(
    ticketId: number,
    includeInternal: boolean = true,
    limit: number = 10,
    offset: number = 0
  ): Promise<ApiResponse<CommentsResponse>> {
    const response = await this.get(
      `${this.basePath}/comment/ticket/${ticketId}?includeInternal=${includeInternal}&limit=${limit}&offset=${offset}`
    );
    return this.extractData(response);
  }

  async getCommentById(id: number): Promise<ApiResponse<Comment>> {
    const response = await this.get(`${this.basePath}/comment/${id}`);
    return this.extractData(response);
  }

  async getRepliesByComment(commentId: number): Promise<ApiResponse<Comment[]>> {
    const response = await this.get(`${this.basePath}/comment/${commentId}/replies`);
    return this.extractData(response);
  }

  async getResolutionComment(ticketId: number): Promise<ApiResponse<Comment>> {
    const response = await this.get(`${this.basePath}/comment/ticket/${ticketId}/resolution`);
    return this.extractData(response);
  }

  async getCommentCount(ticketId: number): Promise<ApiResponse<CommentCount>> {
    const response = await this.get(`${this.basePath}/comment/ticket/${ticketId}/count`);
    return this.extractData(response);
  }

  async saveComment(data: Partial<Comment>): Promise<ApiResponse<Comment>> {
    const response = await this.post(`${this.basePath}/comment`, data);
    return this.extractData(response);
  }

  async deleteComment(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/comment/${id}`);
    return this.extractData(response);
  }

  // Alias for adding members to a team
  async addTeamMembers(teamId: number, userIds: number[]): Promise<ApiResponse<SupportTeamMember[]>> {
    return this.addMultipleTeamMembers(teamId, userIds);
  }

  async removeTeamMember(id: number): Promise<ApiResponse<boolean>> {
    return this.deleteTeamMember(id);
  }

  // ============================================================================
  // CLOSE REASON MASTER APIS
  // ============================================================================

  async getAllCloseReasons(): Promise<ApiResponse<CloseReason[]>> {
    const response = await this.get(`${this.basePath}/close-reason`);
    return this.extractData(response);
  }

  async getActiveCloseReasons(): Promise<ApiResponse<CloseReason[]>> {
    const response = await this.get(`${this.basePath}/close-reason/active`);
    return this.extractData(response);
  }

  async getCloseReasonById(id: number): Promise<ApiResponse<CloseReason>> {
    const response = await this.get(`${this.basePath}/close-reason/${id}`);
    return this.extractData(response);
  }

  async getCloseReasonsByTicketType(ticketTypeId: number): Promise<ApiResponse<CloseReason[]>> {
    const response = await this.get(`${this.basePath}/close-reason/ticket-type/${ticketTypeId}`);
    return this.extractData(response);
  }

  async getDefaultCloseReasonForType(ticketTypeId: number): Promise<ApiResponse<CloseReason>> {
    const response = await this.get(`${this.basePath}/close-reason/ticket-type/${ticketTypeId}/default`);
    return this.extractData(response);
  }

  async saveCloseReason(data: Partial<CloseReason>): Promise<ApiResponse<CloseReason>> {
    const response = await this.post(`${this.basePath}/close-reason`, data);
    return this.extractData(response);
  }

  async deleteCloseReason(id: number): Promise<ApiResponse<boolean>> {
    const response = await this.delete(`${this.basePath}/close-reason/${id}`);
    return this.extractData(response);
  }

  // ============================================================================
  // CLOSE TICKET API
  // ============================================================================

  async closeTicket(ticketId: number, data: CloseTicketRequest): Promise<ApiResponse<Ticket>> {
    const response = await this.put(`${this.basePath}/ticket/${ticketId}/close`, data);
    return this.extractData(response);
  }

  async reopenTicket(ticketId: number, data?: ReopenTicketPayload): Promise<ApiResponse<Ticket>> {
    const response = await this.put(`${this.basePath}/ticket/${ticketId}/reopen`, data || {});
    return this.extractData(response);
  }

  // ============================================================================
  // PAGINATED TICKETS API (Server-Side Pagination)
  // ============================================================================

  /**
   * Get paginated tickets with comprehensive filters
   * Uses POST body for filter parameters
   */
  async getPaginatedTickets(filters: TicketFilters): Promise<PaginatedApiResponse> {
    const response = await this.post(`${this.basePath}/ticket/paginated`, filters);
    return this.extractData(response);
  }

  /**
   * Get all available filter options for the tickets page
   * Returns: applications, ticketTypes, statuses, priorities, closeReasons, categories, supportTeams, supportUsers
   */
  async getFilterOptions(): Promise<ApiResponse<TicketFilterOptions>> {
    const response = await this.get(`${this.basePath}/ticket/filter-options`);
    return this.extractData(response);
  }

  // ============================================================================
  // TICKET HISTORY / TIMELINE APIS
  // ============================================================================

  /**
   * Initialize the history table (run once during setup)
   */
  async initHistoryTable(): Promise<ApiResponse<{ message: string }>> {
    const response = await this.post(`${this.basePath}/ticket/history/init`, {});
    return this.extractData(response);
  }

  /**
   * Get complete timeline/history for a ticket
   */
  async getTicketTimeline(ticketId: number): Promise<ApiResponse<TicketHistoryEntry[]>> {
    const response = await this.get(`${this.basePath}/ticket/${ticketId}/timeline`);
    return this.extractData(response);
  }

  /**
   * Request more information from user for a ticket
   */
  async requestInfo(ticketId: number, data: RequestInfoPayload): Promise<ApiResponse<TicketHistoryEntry>> {
    const response = await this.post(`${this.basePath}/ticket/${ticketId}/request-info`, data);
    return this.extractData(response);
  }

  /**
   * Get pending information requests for a ticket
   */
  async getPendingInfoRequests(ticketId: number): Promise<ApiResponse<TicketHistoryEntry[]>> {
    const response = await this.get(`${this.basePath}/ticket/${ticketId}/pending-info`);
    return this.extractData(response);
  }

  /**
   * User provides information in response to a request
   */
  async provideInfo(historyId: number, data: ProvideInfoPayload): Promise<ApiResponse<TicketHistoryEntry>> {
    const response = await this.put(`${this.basePath}/ticket/info-response/${historyId}`, data);
    return this.extractData(response);
  }

  /**
   * Manually add a history entry
   */
  async addHistoryEntry(data: Partial<TicketHistoryEntry>): Promise<ApiResponse<TicketHistoryEntry>> {
    const response = await this.post(`${this.basePath}/ticket/history`, data);
    return this.extractData(response);
  }

  // ============================================================================
  // FILE UPLOAD APIS
  // ============================================================================

  /**
   * Upload files for helpdesk attachments (tickets & comments)
   * @param files Array of File objects to upload (max 5 files, 10MB each)
   * @returns Array of Attachment metadata with S3 URLs
   */
  async uploadAttachments(files: File[]): Promise<Attachment[]> {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await this.post(`${this.basePath}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const result = this.extractData<Attachment[]>(response);
    return result.data || [];
  }
}

// Export singleton instance
export const helpdeskService = new HelpdeskService();
export default helpdeskService;
